import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { audio } from '../../audio/manager.ts';
import styles from './Stamp.module.css';

const PUFFS = [
  [-1, -0.6],
  [1, -0.5],
  [-0.8, 0.7],
  [0.9, 0.8],
  [0, -1],
  [0, 1],
] as const;

/**
 * A stamp. It comes down after `delay` seconds, makes the sound of one at the moment it lands
 * (when `sound`), and kicks up a few puffs of dust. With "reduce motion" it is simply there.
 */
export function Stamp({
  children,
  tone = 'danger',
  delay = 0,
  tilt = -4,
  sound = false,
  className,
}: {
  children: ReactNode;
  tone?: 'danger' | 'green' | 'amber';
  delay?: number;
  tilt?: number;
  sound?: boolean;
  className?: string;
}) {
  useEffect(() => {
    if (!sound) return;
    const id = setTimeout(
      () => {
        audio.sfx('stamp');
      },
      (delay + 0.23) * 1000,
    );
    return () => {
      clearTimeout(id);
    };
  }, [sound, delay]);

  return (
    <span
      className={[styles['stamp'], tone === 'danger' ? '' : styles[tone], className ?? ''].join(
        ' ',
      )}
      style={{ '--delay': `${delay}s`, '--tilt': `${tilt}deg` } as CSSProperties}
    >
      {children}
      {PUFFS.map(([dx, dy], i) => (
        <span
          key={i}
          className={styles['dust']}
          aria-hidden="true"
          style={{ '--dx': `${dx * 2.4}em`, '--dy': `${dy * 1.6}em` } as CSSProperties}
        />
      ))}
    </span>
  );
}
